/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added new fields: description, family_id, subfamily_id, barcode_1_id, barcode_2_id.
 *                                   - Integrated lookup data for new fields and surcharge.
 *                                   - Ensured `null` handling for optional select fields.
 *                                   - Integrated Spinner for loading states.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide fields
 *                                   and customize field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component handles user input for composite product details.
 *                      It does not process sensitive data directly.
 * INTENT: Provide a reusable form for editing the metadata and pricing details of a composite product,
 *         ensuring data integrity and a clear user experience, now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure all lookup data is correctly integrated
 *                        and that form validation is robust.
 * LLM_CONFIDENCE: 100% - Standard form component with state management and input handling,
 *                        integrated with lookup data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Composite,
  Surcharge,
  FamilyLookupItem,
  SubFamilyLookupItem,
  BarcodeType1LookupItem,
  BarcodeType2LookupItem,
} from '../types/costwand.d';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Input } from './ui/input.tsx';
import { Spinner } from './ui/spinner.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface CompositeFormProps {
  initialData: Composite;
  onSubmit: (data: {
    description: string;
    family_id: number;
    subfamily_id: number | null;
    barcode_1_id: number | null;
    barcode_2_id: number | null;
    tax_profile_id: number;
    markup_perc_wholesale: number;
    markup_perc_retail: number;
    retail_price: number;
  }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  surcharges: Surcharge[];
  families: FamilyLookupItem[];
  subFamilies: SubFamilyLookupItem[];
  barcodeType1s: BarcodeType1LookupItem[];
  barcodeType2s: BarcodeType2LookupItem[];
  lookupLoading: boolean;
  entityId: string; // NEW: Add entityId prop
}

const CompositeForm: React.FC<CompositeFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  surcharges,
  families,
  subFamilies,
  barcodeType1s,
  barcodeType2s,
  lookupLoading,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const [description, setDescription] = useState<string>(initialData.description);
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(initialData.family_id || null);
  const [selectedSubFamilyId, setSelectedSubFamilyId] = useState<number | null>(initialData.subfamily_id || null);
  const [selectedBarcode1Id, setSelectedBarcode1Id] = useState<number | null>(initialData.barcode_1_id || null);
  const [selectedBarcode2Id, setSelectedBarcode2Id] = useState<number | null>(initialData.barcode_2_id || null);
  const [selectedSurchargeId, setSelectedSurchargeId] = useState<number | null>(
    initialData.tax_profile_id || null
  );
  const [wholesaleMarkup, setWholesaleMarkup] = useState<number>(initialData.markup_perc_wholesale);
  const [retailMarkup, setRetailMarkup] = useState<number>(initialData.markup_perc_retail);
  const [retailPrice, setRetailPrice] = useState<number>(initialData.retail_price);

  useEffect(() => {
    setDescription(initialData.description);
    setSelectedFamilyId(initialData.family_id || null);
    setSelectedSubFamilyId(initialData.subfamily_id || null);
    setSelectedBarcode1Id(initialData.barcode_1_id || null);
    setSelectedBarcode2Id(initialData.barcode_2_id || null);
    setSelectedSurchargeId(initialData.tax_profile_id || null);
    setWholesaleMarkup(initialData.markup_perc_wholesale);
    setRetailMarkup(initialData.markup_perc_retail);
    setRetailPrice(initialData.retail_price);
  }, [initialData]);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleSelectChange = useCallback((field: string, value: string) => {
    const parsedValue = value === '' || value === '-' ? null : parseInt(value, 10);
    if (field === 'family_id') setSelectedFamilyId(parsedValue);
    else if (field === 'subfamily_id') setSelectedSubFamilyId(parsedValue);
    else if (field === 'barcode_1_id') setSelectedBarcode1Id(parsedValue);
    else if (field === 'barcode_2_id') setSelectedBarcode2Id(parsedValue);
    else if (field === 'tax_profile_id') setSelectedSurchargeId(parsedValue);
  }, []);

  const handleMarkupChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const parsedValue = parseFloat(value) || 0;
    if (id === 'markup_perc_wholesale') {
      setWholesaleMarkup(parsedValue);
    } else if (id === 'markup_perc_retail') {
      setRetailMarkup(parsedValue);
    }
  }, []);

  const handleRetailPriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const parsedValue = parseFloat(value) || 0;
    setRetailPrice(parsedValue);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (selectedFamilyId !== null && selectedSurchargeId !== null) {
        onSubmit({
          description: description,
          family_id: selectedFamilyId,
          subfamily_id: selectedSubFamilyId,
          barcode_1_id: selectedBarcode1Id,
          barcode_2_id: selectedBarcode2Id,
          tax_profile_id: selectedSurchargeId,
          markup_perc_wholesale: wholesaleMarkup,
          markup_perc_retail: retailMarkup,
          retail_price: retailPrice,
        });
      }
    },
    [
      description,
      selectedFamilyId,
      selectedSubFamilyId,
      selectedBarcode1Id,
      selectedBarcode2Id,
      selectedSurchargeId,
      wholesaleMarkup,
      retailMarkup,
      retailPrice,
      onSubmit,
    ]
  );

  const isDisabled = isSubmitting || lookupLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isFieldVisible('description') && (
        <div>
          <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('description', 'Description')}
          </Label>
          <Input
            id="description"
            type="text"
            required
            value={description}
            onChange={handleDescriptionChange}
            disabled={isDisabled}
          />
        </div>
      )}

      {isFieldVisible('article_number') && (
        <div>
          <Label className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('article_number', 'Article Number')}
          </Label>
          <p className="text-muted-foreground text-sm">{initialData.article_number}</p>
        </div>
      )}
      {isFieldVisible('cost') && (
        <div>
          <Label className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('cost', 'Current Cost')}
          </Label>
          <p className="text-muted-foreground text-sm">{initialData.cost?.toFixed(2) || 'N/A'}</p>
        </div>
      )}

      {isFieldVisible('family_id') && (
        <div>
          <Label htmlFor="family_id" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('family_id', 'Family')}
          </Label>
          <Select
            onValueChange={(value) => handleSelectChange('family_id', value)}
            value={selectedFamilyId ? String(selectedFamilyId) : ''}
            disabled={isDisabled}
            required
          >
            <SelectTrigger id="family_id">
              <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading families...</span> : 'Select a family'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nil" disabled>
                {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading families...</span> : 'Select a family'}
              </SelectItem>
              {families.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isFieldVisible('subfamily_id') && (
        <div>
          <Label htmlFor="subfamily_id" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('subfamily_id', 'Subfamily (Optional)')}
          </Label>
          <Select
            onValueChange={(value) => handleSelectChange('subfamily_id', value)}
            value={selectedSubFamilyId ? String(selectedSubFamilyId) : ''}
            disabled={isDisabled}
          >
            <SelectTrigger id="subfamily_id">
              <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading subfamilies...</span> : 'Select a subfamily (optional)'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-">None</SelectItem>
              {subFamilies.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isFieldVisible('barcode_1_id') && (
        <div>
          <Label htmlFor="barcode_1_id" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('barcode_1_id', 'Barcode Type 1 (Optional)')}
          </Label>
          <Select
            onValueChange={(value) => handleSelectChange('barcode_1_id', value)}
            value={selectedBarcode1Id ? String(selectedBarcode1Id) : ''}
            disabled={isDisabled}
          >
            <SelectTrigger id="barcode_1_id">
              <SelectValue
                placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading barcode types...</span> : 'Select Barcode Type 1 (optional)'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-">None</SelectItem>
              {barcodeType1s.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isFieldVisible('barcode_2_id') && (
        <div>
          <Label htmlFor="barcode_2_id" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('barcode_2_id', 'Barcode Type 2 (Optional)')}
          </Label>
          <Select
            onValueChange={(value) => handleSelectChange('barcode_2_id', value)}
            value={selectedBarcode2Id ? String(selectedBarcode2Id) : ''}
            disabled={isDisabled}
          >
            <SelectTrigger id="barcode_2_id">
              <SelectValue
                placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading barcode types...</span> : 'Select Barcode Type 2 (optional)'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-">None</SelectItem>
              {barcodeType2s.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isFieldVisible('tax_profile_id') && (
        <div>
          <Label htmlFor="tax_profile_id" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('tax_profile_id', 'Surcharge Profile')}
          </Label>
          <Select
            onValueChange={(value) => handleSelectChange('tax_profile_id', value)}
            value={selectedSurchargeId ? String(selectedSurchargeId) : ''}
            disabled={isDisabled}
            required
          >
            <SelectTrigger id="tax_profile_id">
              <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading surcharge profiles...</span> : 'Select a surcharge profile'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nil" disabled>
                {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading surcharge profiles...</span> : 'Select a surcharge profile'}
              </SelectItem>
              {surcharges.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isFieldVisible('markup_perc_wholesale') && (
        <div>
          <Label htmlFor="markup_perc_wholesale" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('markup_perc_wholesale', 'Wholesale Markup %')}
          </Label>
          <Input
            id="markup_perc_wholesale"
            type="number"
            step="0.01"
            required
            value={wholesaleMarkup}
            onChange={handleMarkupChange}
            disabled={isDisabled}
          />
        </div>
      )}

      {isFieldVisible('markup_perc_retail') && (
        <div>
          <Label htmlFor="markup_perc_retail" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('markup_perc_retail', 'Retail Markup %')}
          </Label>
          <Input
            id="markup_perc_retail"
            type="number"
            step="0.01"
            required
            value={retailMarkup}
            onChange={handleMarkupChange}
            disabled={isDisabled}
          />
        </div>
      )}

      {isFieldVisible('retail_price') && (
        <div>
          <Label htmlFor="retail_price" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('retail_price', 'Retail Price')}
          </Label>
          <Input
            id="retail_price"
            type="number"
            step="0.01"
            required
            value={retailPrice}
            onChange={handleRetailPriceChange}
            disabled={isDisabled}
          />
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isDisabled || selectedSurchargeId === null || selectedFamilyId === null}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CompositeForm;